// ─── ПОРЯДОК КВЕСТОВ ПО ПЛОЩАДКАМ ────────────────────────────────────────────
// 25.09 (владелец): «в расстановке у ОО при сортировке по квесту, квесты которые
// проводятся на одной площадке (как форт и кальмар например) — должны стоять
// рядом». Сортировка по алфавиту разрывала пару: «Кальмар» на К, «Форт» на Ф, и
// между ними половина списка — хотя это одна физическая зона, и ОО ставит на них
// одних и тех же людей.
//
// Что считаем «одной площадкой»: linked_groups. Это не догадка — в схеме прямо
// написано «quests that share a physical zone», а в базе лежит группа
// «Форт/Кальмар» ровно из примера владельца. Другого машиночитаемого признака
// площадки у квеста нет: в таблице quests есть только branch_id и sort_order.
//
// Почему не sort_order: его нельзя задать из интерфейса (веб не шлёт это поле ни
// на создание, ни на правку, реордера у ЕО нет), новый квест создаётся с 0 и
// молча встаёт в начало филиала, разорвав пару. Сегодня пары стоят подряд
// случайно-удачно, гарантии нет.
//
// Почему не id группы: конфигуратор при правке связей удаляет все группы квеста и
// создаёт новую, а id — автоинкремент. Доска переставлялась бы сама собой после
// каждой правки связей. Имя группы тоже не годится: оно nullable и в новых
// записях автогенерённое.
//
// Ключ площадки — филиал + минимальный id квеста в КОМПОНЕНТЕ СВЯЗНОСТИ (не «один
// прыжок по группе»): группы могут перекрываться (Дурка/Госпиталь и
// Фазмофобия·Дурка·Госпиталь), и обход на один шаг давал бы несимметричные семьи,
// от чего кластер рвётся посередине. Цена ошибки здесь — только порядок строк,
// поэтому берём заведомо связное замыкание.

export interface VenueQuest { id: string; branch_id: string; short_name?: string | null; name?: string | null }
export interface VenueGroup { branch_id?: string | null; members: string[] }

export interface VenueMap {
  /** Идентификатор площадки для квеста; неизвестный квест — сам себе площадка. */
  zoneId(questId: string, branchId: string): string;
  /** Чем площадка сортируется в алфавите: минимальное короткое имя в кластере. */
  zoneLabel(questId: string, branchId: string): string;
  /** Каноническое короткое имя квеста (не отображаемое — у спецверсии оно своё). */
  questShort(questId: string): string;
}

/** Пустая карта: ведёт себя как «связок нет» — порядок остаётся алфавитным. */
export const EMPTY_VENUE_MAP: VenueMap = {
  zoneId: (q, b) => `${b}|${q}`,
  zoneLabel: (q) => q,
  questShort: (q) => q,
};

export function buildVenueMap(quests: VenueQuest[], groups: VenueGroup[]): VenueMap {
  const short = new Map<string, string>();
  const branchOf = new Map<string, string>();
  for (const q of quests || []) {
    if (!q?.id) continue;
    short.set(q.id, (q.short_name || q.name || q.id).trim() || q.id);
    branchOf.set(q.id, q.branch_id || "");
  }

  /* Union-find по всем группам: связность считаем замыканием, а не одним шагом. */
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    let r = parent.get(x) ?? x;
    while (r !== (parent.get(r) ?? r)) r = parent.get(r) ?? r;
    let cur = x;
    while (cur !== r) { const nxt = parent.get(cur) ?? cur; parent.set(cur, r); cur = nxt; }
    return r;
  };
  const union = (a: string, b: string) => {
    const ra = find(a), rb = find(b);
    if (ra === rb) return;
    /* Корень — лексикографически меньший id: результат не зависит от порядка групп. */
    if (ra < rb) parent.set(rb, ra); else parent.set(ra, rb);
  };
  for (const g of groups || []) {
    const mem = (g?.members || []).filter(Boolean);
    for (let i = 1; i < mem.length; i++) {
      /* Связка живёт внутри филиала; на всякий случай не склеиваем разные филиалы:
         short_name не уникален по сети («Форт» есть и в 20б, и в СВО). */
      if (branchOf.get(mem[i]!) !== branchOf.get(mem[0]!)) continue;
      union(mem[0]!, mem[i]!);
    }
  }

  const labelCache = new Map<string, string>();
  const labelOf = (root: string): string => {
    const hit = labelCache.get(root);
    if (hit !== undefined) return hit;
    let best = "";
    for (const id of short.keys()) {
      if (find(id) !== root) continue;
      const s = short.get(id)!;
      if (!best || s.localeCompare(best, "ru") < 0) best = s;
    }
    const out = best || root;
    labelCache.set(root, out);
    return out;
  };

  return {
    zoneId: (questId, branchId) => {
      const root = parent.has(questId) ? find(questId) : questId;
      return `${branchOf.get(root) ?? branchId ?? ""}|${root}`;
    },
    zoneLabel: (questId, branchId) => {
      const root = parent.has(questId) ? find(questId) : questId;
      return labelOf(root) || short.get(questId) || questId || branchId;
    },
    questShort: (questId) => short.get(questId) || questId,
  };
}

/** Игра на доске глазами сортировки: id квеста, филиал и подпись на карточке. */
export interface VenueSortable { questId: string; branchId: string; shortName: string }

/**
 * Сравнение «по квесту» с учётом площадки: сначала площадки по алфавиту, внутри
 * площадки — квесты. Пара Форт+Кальмар встаёт на «К» и идёт подряд.
 * Ступени до последней нужны, чтобы порядок был устойчив (иначе одинаковые
 * ключи переставляются между перерисовками).
 */
export function compareByVenue(a: VenueSortable, b: VenueSortable, v: VenueMap): number {
  const la = v.zoneLabel(a.questId, a.branchId), lb = v.zoneLabel(b.questId, b.branchId);
  let c = la.localeCompare(lb, "ru");
  if (c !== 0) return c;
  c = v.zoneId(a.questId, a.branchId).localeCompare(v.zoneId(b.questId, b.branchId), "ru");
  if (c !== 0) return c;
  c = v.questShort(a.questId).localeCompare(v.questShort(b.questId), "ru");
  if (c !== 0) return c;
  c = a.questId.localeCompare(b.questId, "ru");
  if (c !== 0) return c;
  /* Последним — отображаемое имя: у спецверсии на карточке стоит её title, и две
     спецверсии одного квеста должны идти в предсказуемом порядке. */
  return a.shortName.localeCompare(b.shortName, "ru");
}
